import React, { useEffect, useState, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, LogOut, ExternalLink, Archive, ChevronUp, ChevronDown } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ConnectFormAssemblyButton from './ConnectFormAssemblyButton';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { FormAssemblyForm, FormBackup } from '@/types/formassembly';
import { 
  checkFormAssemblyConnection, 
  fetchFormAssemblyForms, 
  backupFormAssemblyForm,
  fetchAllUserFormBackups 
} from '@/services/formassemblyService';
import { cn } from '@/lib/utils';
import { isFormNeedsBackup } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTimezone } from "@/contexts/TimezoneContext";
import { useProvider } from "@/contexts/ProviderContext";
import { logger } from '@/lib/logger';

type SortField = 'name' | 'modified' | 'lastBackup';
type SortOrder = 'asc' | 'desc';

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storage = window.localStorage;
    return typeof storage?.getItem === 'function' && typeof storage?.setItem === 'function'
      ? storage
      : null;
  } catch {
    return null;
  }
};

const FormAssemblyFormsList: React.FC = () => {
  const queryClient = useQueryClient();
  const { formatDate } = useTimezone();
  const { refreshConnectionStatus } = useProvider();
  const [backingUpFormId, setBackingUpFormId] = useState<number | null>(null);
  const storage = getStorage();

  // Load sort preferences from localStorage
  const [sortField, setSortField] = useState<SortField>(() => {
    if (!storage) return 'name';
    const saved = storage.getItem('formListSortField');
    return (saved as SortField) || 'name';
  });
  const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
    if (!storage) return 'asc';
    const saved = storage.getItem('formListSortOrder');
    return (saved as SortOrder) || 'asc';
  });
  
  // Save sort preferences to localStorage whenever they change
  useEffect(() => {
    if (!storage) return;
    storage.setItem('formListSortField', sortField);
    storage.setItem('formListSortOrder', sortOrder);
  }, [sortField, sortOrder, storage]);

  const { data: isConnected, isLoading: isLoadingConnectionStatus, error: connectionError, refetch: refetchConnectionStatus } = useQuery<boolean | null, Error>({
    queryKey: ['formAssemblyConnectionStatus'],
    queryFn: checkFormAssemblyConnection,
    staleTime: 1 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
  
  const { data: forms, isLoading: isLoadingForms, error: formsError, refetch: refetchForms } = useQuery<FormAssemblyForm[], Error>({
    queryKey: ['formAssemblyForms'],
    queryFn: fetchFormAssemblyForms,
    enabled: isConnected === true,
  });

  const { data: allUserBackups, isLoading: isLoadingAllUserBackups } = useQuery<FormBackup[], Error>({
    queryKey: ['allUserFormBackups'],
    queryFn: fetchAllUserFormBackups,
    enabled: isConnected === true, // Only fetch if connected
  });

  useEffect(() => {
    if (isConnected === true) {
      queryClient.invalidateQueries({ queryKey: ['formAssemblyForms'] });
      queryClient.invalidateQueries({ queryKey: ['allUserFormBackups'] });
    }
  }, [isConnected, queryClient]);

  const backupFormMutation = useMutation<
    { success: boolean; backupId: string; message: string },
    Error,
    { formId: number; formName: string }
  >({
    mutationFn: ({ formId }) => backupFormAssemblyForm(formId),
    onMutate: ({ formId }) => {
      setBackingUpFormId(formId);
    },
    onSuccess: (_, variables) => {
      toast({
        title: "Backup Successful",
        description: `Form '${variables.formName}' (ID: ${variables.formId}) backed up successfully`,
      });
      queryClient.invalidateQueries({ queryKey: ['formBackups', variables.formId.toString()] });
      queryClient.invalidateQueries({ queryKey: ['allUserFormBackups'] }); // Invalidate all backups query
    },
    onError: (error, variables) => {
      if (error.message === "REAUTH_REQUIRED") {
        toast({
          variant: "destructive",
          title: "Backup Failed",
          description: "Re-authentication required. Please disconnect and connect FormAssembly again."
        });
        return;
      }

      // Extract form ID from error message if present
      const formIdMatch = error.message.match(/form (\d+)/i);
      const formId = formIdMatch ? formIdMatch[1] : variables.formId;

      // Create mailto link with pre-populated subject and body
      const subject = encodeURIComponent(`Form Backup Error - Form ID ${formId}`);
      const body = encodeURIComponent(`I encountered an error while backing up a form:

Form ID: ${formId}
Error: ${error.message}

Please help me resolve this issue.`);
      const mailtoLink = `mailto:support@fab.day?subject=${subject}&body=${body}`;

      toast({
        variant: "destructive",
        title: "Backup Failed",
        description: (
          <div className="space-y-2">
            <p>{error.message || `Could not backup form ${formId}.`}</p>
            <p className="text-sm">
              Need help?{" "}
              <a
                href={mailtoLink}
                className="underline hover:text-white"
                onClick={(e) => e.stopPropagation()}
              >
                Contact support@fab.day
              </a>
            </p>
          </div>
        ),
      });
    },
    onSettled: () => {
      setBackingUpFormId(null);
    }
  });

  const handleDisconnect = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({ variant: "destructive", title: "Error", description: "You are not logged in." });
      return;
    }
    try {
      toast({ title: "Disconnecting...", description: "Removing FormAssembly connection." });
      const { error: deleteError } = await supabase
        .from('user_formassembly_credentials')
        .delete()
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      toast({ title: "Disconnected", description: "Successfully disconnected from FormAssembly." });
      queryClient.setQueryData(['formAssemblyConnectionStatus'], false);
      queryClient.invalidateQueries({ queryKey: ['formAssemblyConnectionStatus'] });
      queryClient.invalidateQueries({ queryKey: ['formAssemblyForms'] });
      queryClient.invalidateQueries({ queryKey: ['allUserFormBackups'] });
      refreshConnectionStatus('formassembly');
    } catch (error: unknown) {
      logger.error("Error disconnecting FormAssembly:", error);
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      toast({ variant: "destructive", title: "Disconnection Failed", description: errorMessage });
    }
  };

  const handleBackupForm = (form: FormAssemblyForm) => {
    if (form.id) {
      backupFormMutation.mutate({ formId: form.id, formName: form.name });
    } else {
      toast({ variant: "destructive", title: "Error", description: "Selected form has no ID, cannot backup." });
    }
  };


  const formatDateTimeSplit = (dateString: string | null): { date: string; time: string } => {
    if (!dateString) return { date: 'N/A', time: '' };
    try {
      const date = formatDate(dateString, 'MMM d, yyyy');
      const time = formatDate(dateString, 'h:mm a');
      return { date, time };
    } catch {
      return { date: 'N/A', time: '' };
    }
  };

  const getLatestBackupTimestamp = (formId: number, backups: FormBackup[] | undefined): number => {
    if (!backups || backups.length === 0) return 0;
    const formBackups = backups.filter(b => b.form_id_external === formId.toString());
    if (formBackups.length === 0) return 0;
    try {
      return new Date(formBackups[0].created_at).getTime();
    } catch {
      return 0;
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedForms = useMemo(() => {
    if (!forms) return [];
    
    return [...forms].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;
      
      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'modified':
          aValue = new Date(a.modified || 0).getTime();
          bValue = new Date(b.modified || 0).getTime();
          break;
        case 'lastBackup':
          aValue = getLatestBackupTimestamp(a.id, allUserBackups);
          bValue = getLatestBackupTimestamp(b.id, allUserBackups);
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [forms, allUserBackups, sortField, sortOrder]);

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <TableHead 
      className="cursor-pointer hover:bg-muted/50 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && (
          sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
        )}
      </div>
    </TableHead>
  );

  if (isLoadingConnectionStatus || (isConnected && (isLoadingForms || isLoadingAllUserBackups))) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="ml-2 text-muted-foreground">
          {isLoadingConnectionStatus ? "Checking connection..." : "Loading forms..."}
        </p>
      </div>
    );
  }

  if (connectionError) {
     return (
      <div className="container mx-auto py-8 text-center">
        <Alert variant="destructive">
          <AlertTitle>Connection Check Failed</AlertTitle>
          <AlertDescription>
            Could not verify FormAssembly connection: {connectionError.message}.
            <Button variant="link" onClick={() => refetchConnectionStatus()} className="ml-2">Try again</Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  if (isConnected === null) { 
     return (
      <div className="container mx-auto py-8 text-center">
        <Alert variant="default"> 
          <AlertTitle>Connection Status Unknown</AlertTitle>
          <AlertDescription>
            Could not determine FormAssembly connection status.
             <Button variant="link" onClick={() => refetchConnectionStatus()} className="ml-2">Refresh Status</Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="container mx-auto py-8 text-center">
        <Alert>
          <AlertTitle className="flex items-center justify-center"><ExternalLink className="h-5 w-5 mr-2"/>Connect Form Provider</AlertTitle>
          <AlertDescription>
            To view and manage your forms, please connect your account.
            This will redirect you to your form provider to authorize access.
          </AlertDescription>
        </Alert>
        <div className="mt-6">
          <ConnectFormAssemblyButton />
        </div>
      </div>
    );
  }
  
  if (formsError) {
     if (formsError.message === "REAUTH_REQUIRED") {
        return (
          <div className="container mx-auto py-8 text-center">
            <Alert variant="destructive">
              <AlertTitle>Reconnection Required</AlertTitle>
              <AlertDescription>
                Your FormAssembly connection has expired or is invalid. Please connect again.
              </AlertDescription>
            </Alert>
             <div className="mt-6 flex flex-col items-center gap-4">
              <ConnectFormAssemblyButton />
              <Button variant="outline" onClick={handleDisconnect}>
                <LogOut className="mr-2 h-4 w-4" /> Disconnect (Clear Old Connection)
              </Button>
            </div>
          </div>
        );
    }
    return (
       <div className="container mx-auto py-8">
        <Alert variant="destructive">
          <AlertTitle>Error Loading Forms</AlertTitle>
          <AlertDescription>
            Could not load forms from FormAssembly: {formsError.message}
            <Button variant="link" onClick={() => refetchForms()} className="ml-2">Try again</Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="container mx-auto py-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold">Your FormAssembly Forms</h2>
            <a
              href="https://app.formassembly.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline flex items-center gap-1 text-sm"
            >
              <ExternalLink className="h-4 w-4" />
              Open FormAssembly
            </a>
          </div>
          <Button variant="outline" onClick={handleDisconnect}>
            <LogOut className="mr-2 h-4 w-4" /> Disconnect FormAssembly
          </Button>
        </div>
        {(!forms || forms.length === 0) ? (
          <Alert>
            <AlertTitle>No Forms Found</AlertTitle>
            <AlertDescription>
            No forms were found in your FormAssembly account, or there was an issue fetching them.
            <Button variant="link" onClick={() => refetchForms()} className="ml-2">Try refreshing the list</Button>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="overflow-x-auto">
            <Table>
            <TableHeader>
              <TableRow>
                <SortableHeader field="name">Name</SortableHeader>
                <SortableHeader field="modified">Form Last Modified</SortableHeader>
                <SortableHeader field="lastBackup">Last Backup</SortableHeader>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedForms.map((form) => {
                const needsBackup = isFormNeedsBackup(form.modified, allUserBackups?.find(b => b.form_id_external === form.id.toString())?.created_at || null);
                return (
                  <TableRow key={form.id}>
                    <TableCell className="font-medium">
                      {form.name.length > 75 ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link to={`/formassembly/${form.id}`} className="hover:underline text-primary">
                              {form.name.substring(0, 75)}...
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{form.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Link to={`/formassembly/${form.id}`} className="hover:underline text-primary">
                          {form.name}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell className={cn(needsBackup && 'text-red-500')}>
                      {needsBackup ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex flex-col">
                              <span>{formatDateTimeSplit(form.modified).date}</span>
                              <span className="text-sm text-muted-foreground">{formatDateTimeSplit(form.modified).time}</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Please backup soon. Your latest edits are not backed up currently.</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <div className="flex flex-col">
                          <span>{formatDateTimeSplit(form.modified).date}</span>
                          <span className="text-sm text-muted-foreground">{formatDateTimeSplit(form.modified).time}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const backups = allUserBackups?.filter(b => b.form_id_external === form.id.toString());
                        if (!backups || backups.length === 0) return 'N/A';
                        const { date, time } = formatDateTimeSplit(backups[0].created_at);
                        return (
                          <div className="flex flex-col">
                            <span>{date}</span>
                            <span className="text-sm text-muted-foreground">{time}</span>
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        onClick={() => handleBackupForm(form)}
                        disabled={backingUpFormId === form.id}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        {backingUpFormId === form.id ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <Archive className="h-4 w-4 mr-1" />} Backup
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default FormAssemblyFormsList;
